import { DocumentVisibilityTracker } from '@pulse/browser-shared';
import {
    PulseBatchConfig,
    PulseMessage,
    PulseMessagePriority,
    TrackerConfigurations,
} from '@pulse/common';
import {
    BehaviorSubject,
    Subject,
    buffer,
    combineLatest,
    filter,
    from,
    interval,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { TrackingConsentService } from '../services';
import { getBatches, getTrackerBatchConfig } from './batch-helper';
import { BatchQueue } from './batch-queue';

export class MessageQueue {
    readonly queueEmpty$: Subject<boolean>;
    private trackingEnabled?: boolean;
    private batchConfig: PulseBatchConfig;

    private messageReceived = new Subject<PulseMessage>();
    private flushRequested = new Subject<void>();

    constructor(
        private consentService: TrackingConsentService,
        private config: TrackerConfigurations,
        private batchQueue: BatchQueue,
        private visibility: DocumentVisibilityTracker,
    ) {
        this.queueEmpty$ = batchQueue.queueEmpty$;
        this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);

        this.consentService.trackingEnabled().subscribe((enabled) => {
            this.trackingEnabled = enabled;
            this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);
        });

        this.visibility.onVisibilityChange().subscribe(async (visible) => {
            if (!visible && this.trackingEnabled) {
                this.flush();
                this.batchQueue.stopQueue();
            } else if (visible && this.trackingEnabled) {
                this.batchQueue.startQueue();
            }
        });

        this.start();
    }

    /**
     * Emit an event.
     * Event will be pushed into queue and will be scheduled to transmission.
     */
    push(...messages: PulseMessage[]) {
        const force = messages.some((_) => _.priority === PulseMessagePriority.Force);
        const immediate = messages.some((_) => _.priority === PulseMessagePriority.High) || force;

        if (!this.trackingEnabled && !force) {
            return;
        }

        messages.forEach((message) => this.messageReceived.next(message));
        if ((immediate && this.trackingEnabled) || force) {
            // In case of navigation events for example
            // the event is published right before the tab is closed
            // Hence, using doPublishSync to publish the message immediately with keepalive flag
            this.flushRequested.next();
        }

        return;
    }

    /**
     * Signals the messages subscription to immediately compose batches from
     *  the message we currently have in the message queue
     */
    flush(): void {
        this.flushRequested.next();
    }

    /**
     * Starts an observable that listens to events (published via messageReceived subject)
     * And will buffer the events till they reach a certain batchSize, or a time limit expires
     *
     * It will flush all immediately if flushRequested subject emits, then it will re-listen recursively
     * as takeUntil operator will result in the completion and destruction of the original subscription
     */
    private start() {
        const renewInterval$ = new Subject<void>();
        const bufferInterval$ = renewInterval$.pipe(
            startWith({}),
            switchMap(() => interval(this.config.batchTimeMS)),
        );
        const bufferCount$ = new BehaviorSubject<number>(0);
        const bufferCountReached$ = new Subject<void>();
        bufferCount$.pipe(filter((count) => count === this.config.batchSize + 1)).subscribe(() => {
            bufferCountReached$.next();
        });
        const withrowBuffer$ = combineLatest([
            bufferInterval$,
            bufferCountReached$,
            this.flushRequested,
        ]);

        from(this.messageReceived)
            .pipe(
                tap(() => bufferCount$.next(bufferCount$.value + 1)),
                buffer(withrowBuffer$),
            )
            .subscribe((messages) => {
                bufferCount$.next(0);
                renewInterval$.next();
                this.pushToBatchQueue(messages);
            });

        bufferCount$.next(0);
        bufferCountReached$.next();
        this.flushRequested.next();
        renewInterval$.next();
    }

    private pushToBatchQueue(messages: PulseMessage[]): void {
        const batches = getBatches(messages, this.batchConfig);

        if (!batches.length) {
            return;
        }

        batches.map((batch) => this.batchQueue.pushBatch(batch));
    }
}
